import { AnimatePresence, SVGMotionProps, motion } from 'framer-motion';
import styled from 'styled-components';

import { theme } from '@styles/theme';

import { CheckboxState } from './checkbox';

export type AnimatedCheckboxProps = SVGMotionProps<SVGSVGElement> & {
  checked?: CheckboxState;
  isDisabled?: boolean;
  isFocused?: boolean;
};

export const AnimatedCheckbox = ({
  isDisabled = false,
  isFocused = false,
  checked = false,
  ...rest
}: AnimatedCheckboxProps) => {
  return (
    <Svg
      aria-hidden="true"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      {isFocused && (
        <rect
          x="1"
          y="1"
          width="22"
          height="22"
          rx="3"
          stroke={theme.color.elementBlue100}
          strokeWidth="2"
        />
      )}

      {checked === false && !isDisabled && (
        <Path
          d="M20 4V20H4V4H20ZM19.7778 2H4.22222C3 2 2 3 2 4.22222V19.7778C2 21 3 22 4.22222 22H19.7778C21 22 22 21 22 19.7778V4.22222C22 3 21 2 19.7778 2Z"
          fill={theme.color.interactiveElementNeutral}
        />
      )}

      {checked === false && isDisabled && (
        <path
          d="M19.7778 2H4.22222C3 2 2 3 2 4.22222V19.7778C2 21 3 22 4.22222 22H19.7778C21 22 22 21 22 19.7778V4.22222C22 3 21 2 19.7778 2Z"
          fill={theme.color.interactiveElementNeutral}
        />
      )}

      <AnimatePresence initial={false}>
        {checked && (
          <Path
            key="container"
            variants={containerVariants}
            fill={
              isDisabled
                ? theme.color.interactiveElementNeutral
                : theme.color.interactiveElementSelected
            }
            d="M19.7778 2H4.22222C3 2 2 3 2 4.22222V19.7778C2 21 3 22 4.22222 22H19.7778C21 22 22 21 22 19.7778V4.22222C22 3 21 2 19.7778 2Z"
            initial="hidden"
            animate="visible"
          />
        )}

        {checked === true && (
          <motion.polyline
            key="checkmark"
            fill="none"
            stroke={theme.color.white100}
            strokeWidth={2}
            strokeDasharray={20}
            points="5.8 11.7 10 16 18.2 7.5"
            variants={checkVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          />
        )}

        {checked === 'mixed' && (
          <motion.rect
            y="11"
            fill={theme.color.white100}
            variants={dashVariants}
            initial="hidden"
            animate="visible"
          />
        )}
      </AnimatePresence>
    </Svg>
  );
};

const Svg = styled(motion.svg)({
  flexShrink: 0,
});

const Path = styled(motion.path)({
  transition: 'fill 0.2s ease-out',
});

const dashVariants = {
  hidden: { width: 0, height: 1, x: 12 },
  visible: { width: 12, height: 2, x: 6 },
};

const checkVariants = {
  hidden: { strokeDashoffset: 20 },
  visible: { strokeDashoffset: 0 },
  exit: { strokeDashoffset: 20, transition: { duration: 0.1 } },
};

const containerVariants = {
  hidden: { scale: 0.8 },
  visible: { scale: 1 },
};
